import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { createOrganization } from '../pages/Onboarding/utils/onboardingApi';

/**
 * OrganizationRequiredModal
 *
 * Blocking modal shown when an authenticated user has completed onboarding
 * but has no organization (e.g., after being removed from their org).
 * Forces the user to create a new organization before using any features.
 */
const OrganizationRequiredModal: React.FC = () => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const toast = useToast();

  const [orgName, setOrgName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Only show for users who completed onboarding but have no organization
  if (!user || !user.onboardingCompleted || user.organizationId) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = orgName.trim();

    if (!trimmedName) {
      setValidationError(t('organizationRequired.errorRequired', 'Organization name is required'));
      return;
    }

    if (trimmedName.length < 2) {
      setValidationError(t('organizationRequired.errorTooShort', 'Organization name must be at least 2 characters'));
      return;
    }

    setValidationError('');
    setIsSubmitting(true);

    try {
      await createOrganization({ name: trimmedName });

      // Reload page to refresh all contexts with the new organization data
      // (same pattern as OrganizationContext.switchOrganization)
      window.location.reload();
    } catch (error) {
      toast.errorMapped(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop - no onClick dismiss */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl transform transition-all">
          {/* Header - no close button */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('organizationRequired.title', 'Organization Required')}
              </h2>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                {t('organizationRequired.description', 'You have been removed from your previous organization. To continue using SailLab, please create a new organization.')}
              </p>

              <div>
                <label htmlFor="org-name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('organizationRequired.orgNameLabel', 'Organization Name')}
                </label>
                <input
                  id="org-name"
                  type="text"
                  value={orgName}
                  onChange={(e) => {
                    setOrgName(e.target.value);
                    if (validationError) setValidationError('');
                  }}
                  placeholder={t('organizationRequired.orgNamePlaceholder', 'e.g., Acme Corporation')}
                  disabled={isSubmitting}
                  className="input-base w-full"
                  autoFocus
                />
                {validationError && (
                  <p className="mt-1 text-sm text-red-600">{validationError}</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('organizationRequired.submitting', 'Creating...')}
                  </>
                ) : (
                  t('organizationRequired.submit', 'Create Organization')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrganizationRequiredModal;
